package com.sddamico.swigmore.ds.poll

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.SwEffects
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.forms.IconButton
import com.sddamico.swigmore.ds.forms.IconButtonVariant
import com.sddamico.swigmore.ds.stitch
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeDisplay
import com.sddamico.swigmore.ds.typeTeam
import com.varabyte.kobweb.compose.dom.svg.Circle
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element
import org.w3c.dom.pointerevents.PointerEvent
import kotlin.math.abs

/**
 * Drag-and-drop hooks for a ballot slot; the page owns the ballot state. Targets are 0-based slot
 * indexes: a slot reports drops of field teams onto itself, and while it is being dragged, the slot
 * last under the pointer.
 */
class SlotDragHandlers(
  val onDragStart: () -> Unit = {},
  val onDragOver: (target: Int) -> Unit = {},
  val onDrop: (target: Int) -> Unit = {},
  val onDragEnd: () -> Unit = {},
)

/**
 * One numbered position on "Pick your eight." A filled slot is cream with the stitch and offers
 * ▲ ▼ × controls plus a drag grip. An empty slot is a faint dashed well. While something is dragged
 * over it, the slot lights up gold.
 *
 * Reordering uses pointer events rather than HTML drag and drop, which phones don't fire for touch.
 * A mouse drags from anywhere on the slot; a finger drags from the grip, so swiping elsewhere on
 * the slot still scrolls the page.
 */
@Composable
fun BallotSlot(
  position: Int,
  team: String?,
  modifier: Modifier = Modifier,
  meta: String? = null,
  dropTarget: Boolean = false,
  dragging: Boolean = false,
  locked: Boolean = false,
  canMoveUp: Boolean = true,
  canMoveDown: Boolean = true,
  onMoveUp: (() -> Unit)? = null,
  onMoveDown: (() -> Unit)? = null,
  onRemove: (() -> Unit)? = null,
  drag: SlotDragHandlers? = null,
) {
  val filled = team != null
  val pointer = remember { PointerDrag() }
  val inkOnSlot = filled || dropTarget
  Div(
    attrs = Modifier
      .css(
        "display" to "grid", "grid-template-columns" to "40px minmax(0,1fr) auto", "align-items" to "center",
        "gap" to "12px", "padding" to "10px 12px", "border-radius" to SwEffects.radiusCard, "min-height" to "34px",
        "color" to SwColor.field700, "transition" to "background .12s",
        "background" to when {
          dropTarget -> SwColor.gold
          filled -> SwColor.cream
          else -> SwColor.tintOnField
        },
        "opacity" to if (dragging) ".4" else "1",
        "cursor" to if (filled && !locked) "grab" else "default",
      )
      .stitch(if (filled) SwColor.stitch else "rgba(239,229,201,.3)", "5px")
      .then(modifier)
      .toAttrs {
        attr("aria-label", "Ballot position $position: ${team ?: "empty"}")
        attr(SLOT_ATTR, (position - 1).toString())
        if (drag != null && !locked) {
          onDragOver { ev -> ev.preventDefault(); drag.onDragOver(position - 1) }
          onDrop { ev -> ev.preventDefault(); drag.onDrop(position - 1) }
          if (filled) {
            fun hover() {
              slotAt(pointer.x, pointer.y)?.let { pointer.target = it; drag.onDragOver(it) }
            }
            fun scrollWhileNearEdge() {
              if (scrollNearEdge(pointer.y)) hover()
              pointer.frame = window.requestAnimationFrame { scrollWhileNearEdge() }
            }
            addEventListener("pointerdown") {
              val ev = it.nativeEvent as PointerEvent
              val target = ev.target as? Element ?: return@addEventListener
              if (ev.button.toInt() != 0 || target.closest("button") != null) return@addEventListener
              if (ev.pointerType != "mouse" && target.closest("[$GRIP_ATTR]") == null) return@addEventListener
              ev.preventDefault()
              (ev.currentTarget as Element).setPointerCapture(ev.pointerId)
              pointer.startX = ev.clientX
              pointer.startY = ev.clientY
              pointer.target = position - 1
            }
            // A press is a drag only once the pointer moves, so pressing a slot near the window edge
            // doesn't scroll the page.
            addEventListener("pointermove") {
              val ev = it.nativeEvent as PointerEvent
              if (!(ev.currentTarget as Element).hasPointerCapture(ev.pointerId)) return@addEventListener
              pointer.x = ev.clientX
              pointer.y = ev.clientY
              if (!pointer.started) {
                if (abs(pointer.x - pointer.startX) + abs(pointer.y - pointer.startY) < DRAG_START_PX) return@addEventListener
                pointer.started = true
                drag.onDragStart()
                scrollWhileNearEdge()
              }
              hover()
            }
            // Releasing off the list still drops on the slot that was last lit up.
            addEventListener("pointerup") {
              val ev = it.nativeEvent as PointerEvent
              if (pointer.started && (ev.currentTarget as Element).hasPointerCapture(ev.pointerId)) drag.onDrop(pointer.target)
            }
            addEventListener("lostpointercapture") {
              if (!pointer.started) return@addEventListener
              pointer.started = false
              window.cancelAnimationFrame(pointer.frame)
              drag.onDragEnd()
            }
          }
        }
      }
  ) {
    Div(
      attrs = Modifier.typeDisplay("26px").css("text-align" to "center",
        "color" to if (inkOnSlot) SwColor.field700 else "rgba(239,229,201,.4)").toAttrs()
    ) { Text(position.toString()) }
    Div(attrs = Modifier.css("min-width" to "0").toAttrs()) {
      Div(
        attrs = Modifier.typeTeam("15px").css("color" to if (inkOnSlot) SwColor.field700 else "rgba(239,229,201,.55)").toAttrs()
      ) { Text(team ?: "Drop a team here") }
      Div(
        attrs = Modifier.typeBody("11px", 600, "normal").css("opacity" to ".65", "margin-top" to "2px",
          "color" to if (inkOnSlot) SwColor.field700 else SwColor.cream).toAttrs()
      ) { Text(meta ?: if (filled) "" else "Drag from the field or tap +") }
    }
    if (filled && !locked) {
      Div(attrs = Modifier.css("display" to "flex", "gap" to "4px", "align-items" to "center").toAttrs()) {
        IconButton("▲", "Move up", Modifier.css("opacity" to if (canMoveUp) "1" else ".35"), onClick = onMoveUp.takeIf { canMoveUp })
        IconButton("▼", "Move down", Modifier.css("opacity" to if (canMoveDown) "1" else ".35"), onClick = onMoveDown.takeIf { canMoveDown })
        IconButton("×", "Remove", variant = IconButtonVariant.Bare, onClick = onRemove)
        Div(
          attrs = Modifier.css("width" to "28px", "align-self" to "stretch", "display" to "flex",
            "align-items" to "center", "justify-content" to "center",
            "opacity" to ".55", "cursor" to "grab", "touch-action" to "none",
            // A press-and-hold that starts a text selection or the iOS callout cancels the drag.
            "user-select" to "none", "-webkit-user-select" to "none", "-webkit-touch-callout" to "none")
            .toAttrs { attr("aria-hidden", "true"); attr(GRIP_ATTR, "") }
        ) { DotGridIcon() }
      }
    }
  }
}

@Composable
private fun DotGridIcon() {
  Svg(attrs = { viewBox(0, 0, 10, 16); attr("width", "10"); attr("height", "16") }) {
    for (cx in listOf(2, 8)) for (cy in listOf(2, 8, 14)) Circle {
      attr("cx", "$cx"); attr("cy", "$cy"); attr("r", "1.6"); attr("fill", "currentColor")
    }
  }
}

private const val SLOT_ATTR = "data-ballot-slot"
private const val GRIP_ATTR = "data-ballot-grip"
private const val DRAG_START_PX = 4
private const val EDGE_PX = 60
private const val EDGE_SCROLL_PX = 12.0

private class PointerDrag {
  var started = false
  var startX = 0
  var startY = 0
  var x = 0
  var y = 0
  var target = 0
  var frame = 0
}

private fun slotAt(x: Int, y: Int): Int? =
  document.elementFromPoint(x.toDouble(), y.toDouble())?.closest("[$SLOT_ATTR]")?.getAttribute(SLOT_ATTR)?.toInt()

/**
 * Slots below the fold on a phone are only reachable if the page scrolls while dragging, including
 * while the finger holds still at the edge. Returns whether it scrolled.
 */
private fun scrollNearEdge(y: Int): Boolean {
  val dy = when {
    y < EDGE_PX -> -EDGE_SCROLL_PX
    y > window.innerHeight - EDGE_PX -> EDGE_SCROLL_PX
    else -> return false
  }
  window.scrollBy(0.0, dy)
  return true
}
